import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Alert, AlertTitle } from '@mui/material';
import usePermisos from '../hooks/usePermisos';

/**
 * Bloquea el acceso a una pantalla si el usuario no tiene ninguno de los
 * permisos granulares indicados (los mismos que se usan en el Navbar
 * para mostrar/ocultar el menú y en el backend para gatear la API).
 * Superadmin siempre pasa (lo resuelve tienePermiso()).
 *
 * Por defecto NO redirige a ningún lado: el usuario sigue logueado y
 * simplemente se le muestra un aviso en el lugar de la pantalla
 * bloqueada. Esto es a propósito — redirigir a /login generaba el
 * efecto de "te echa de la sesión" apenas alguien sin el permiso caía
 * en esa pantalla. Si en cambio conviene mandarlo a una pantalla
 * concreta dentro de la app (ej. una bienvenida genérica), pasá
 * `redirectTo` con esa ruta — nunca a /login.
 *
 * Uso:
 *   <RequirePermiso checks={[{ modulo: 'dashboard', permiso: 'ver' }]}>
 *     <Dashboard />
 *   </RequirePermiso>
 *
 *   // con OR entre varios permisos:
 *   <RequirePermiso checks={[
 *     { modulo: 'recibos', permiso: 'ver_propios' },
 *     { modulo: 'recibos', permiso: 'ver' }
 *   ]}>
 *     <Recibos />
 *   </RequirePermiso>
 *
 *   // redirigiendo en vez de mostrar el aviso:
 *   <RequirePermiso checks={[...]} redirectTo="/bienvenida">
 *     <Dashboard />
 *   </RequirePermiso>
 */
const RequirePermiso = ({ checks, children, redirectTo }) => {
  const { tienePermiso, loading } = usePermisos();

  // Mientras se cargan los permisos no se decide nada todavía,
  // para no bloquear de más en el primer render.
  if (loading) {
    return null;
  }

  const autorizado = checks.some(({ modulo, permiso }) => tienePermiso(modulo, permiso));

  if (!autorizado) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <Alert severity="warning" sx={{ maxWidth: 500 }}>
          <AlertTitle>Sin permiso para ver esta sección</AlertTitle>
          Tu usuario no tiene habilitado el acceso a esta pantalla. Si creés que es un error, contactá a un administrador.
        </Alert>
      </Box>
    );
  }

  return children;
};

export default RequirePermiso;
